import { inject, Injectable } from '@angular/core';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { Router } from '@angular/router';
import { MealService } from '@shared/services/meal.service';
import { Order, OrderProduct } from '@views/meal/shared';
import {
  ErrorContext,
  errorMessages,
  OrderErrorType,
  ProductsFormattedFromError,
} from '@shared/enum/order-error';
import { TranslateService } from '@ngx-translate/core';

interface CreateOrderError {
  code: string;
  description: string;
  details?: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  snackBarService = inject(SnackBarService);
  router = inject(Router);
  mealService = inject(MealService);
  translateService = inject(TranslateService);

  get order(): Order {
    return this.mealService.order;
  }

  handleError(error: CreateOrderError): void {
    console.log('error', error);
    const errorDetails = error.details || {};
    const errorType = errorDetails['order-message'] || {};

    const productFormattedFromError = Object.entries(errorDetails)
      .filter(([key]) => key !== 'order-message')
      .map(([productId, quantityLeft]) => ({
        productId,
        quantityLeft: +quantityLeft,
      }));

    const productLabels = this.getProductsLabels(
      productFormattedFromError,
      this.order.productsInOrder
    );

    console.log(productLabels);
    console.log(productFormattedFromError);

    const errorContext = {
      amountMin: error?.details?.OrderMinimalAmount || 0,
      amountMax: error?.details?.OrderPaymentMethod || 0,
      maxItems: error?.details?.OrderMaxCoreMenu || 0,
      productFormattedFromError,
      productLabels,
    };

    const productsToRemove =
      errorContext.productFormattedFromError.map(
        ({ productId }) => productId
      ) || [];

    const errorMessage = errorType
      ? this.getErrorMessage(
          errorType as OrderErrorType,
          errorContext as ErrorContext
        )
      : 'errors.generic';

    this.dispatchErrorActions(
      errorType as OrderErrorType,
      productsToRemove,
      productFormattedFromError,
      this.order.productsInOrder
    );

    this.snackBarService.error(errorMessage);
  }

  getErrorMessage = (
    errorType: OrderErrorType,
    context: ErrorContext
  ): string => {
    const productsLabels = context.productLabels?.join(', ');

    const productErrorMapping: Partial<Record<OrderErrorType, string>> = {
      [OrderErrorType.ProductStockUnknownError]: 'errors.productStockUnknown',
      [OrderErrorType.SellingModeNotRespectedError]:
        'errors.sellingModeNotRespected',
    };

    const errorKey = productErrorMapping[errorType];

    if (errorKey) {
      return this.translateService.instant(errorKey, {
        productName: productsLabels,
      });
    }

    if (errorType === OrderErrorType.StockInsufficientError) {
      const quantity = context.productFormattedFromError?.[0].quantityLeft || 0;
      const messageKey =
        quantity > 0
          ? 'errors.insufficientStock.available'
          : 'errors.insufficientStock.unavailable';

      return this.translateService.instant(messageKey, {
        ...context,
        productName: productsLabels,
        quantity,
      });
    }

    const errorMessageKey = errorMessages[errorType]
      ? errorMessages[errorType]
      : 'errors.generic';
    return this.translateService.instant(errorMessageKey, {
      productName: productsLabels,
      maxItems: context.maxItems,
      amountMax: context.amountMax,
      amountMin: context.amountMin,
    });
  };

  getProductsLabels(
    productsFormattedFromErrors: ProductsFormattedFromError[],
    productsFromCart: OrderProduct[]
  ) {
    return productsFormattedFromErrors.map(({ productId }) => {
      const product = productsFromCart.find((p) => p.product.id === productId);
      return product?.product?.label || 'Unknown';
    });
  }

  dispatchErrorActions(
    errorType: OrderErrorType,
    productsToRemove: string[],
    productsFormattedFromError: ProductsFormattedFromError[],
    productsFromCart: OrderProduct[]
  ) {
    if (
      errorType === OrderErrorType.TotalMinimalAmountError ||
      errorType === OrderErrorType.SellingModeNotRespectedError ||
      errorType === OrderErrorType.NegatifPriceError ||
      errorType === OrderErrorType.FormulaOnlyError
    ) {
      // navigate to menu
      this.router.navigate(
        [`meals/${this.mealService.shop?.id}/menu/${this.mealService.cardId}`],
        { queryParams: { date: this.mealService.dateSelected } }
      );
    } else if (errorType === OrderErrorType.OrderHashAlreadyExistsError) {
      // navigate to my orders page
      this.router.navigate(['/meals/my-orders']);
    } else if (
      errorType === OrderErrorType.ShopInactiveError ||
      errorType === OrderErrorType.SalePeriodError ||
      errorType === OrderErrorType.ServiceDateDeadlineError
    ) {
      // navigate to home page
      this.router.navigate([`/${this.mealService.shop.id}`]);
    } else if (
      errorType === OrderErrorType.ProductStockUnknownError ||
      errorType === OrderErrorType.ProductUnknownError ||
      errorType === OrderErrorType.ProductNotInShopCatalogError
    ) {
      this.removeProductsFromOrder(productsToRemove);
      this.router.navigate(
        [`meals/${this.mealService.shop?.id}/menu/${this.mealService.cardId}`],
        { queryParams: { date: this.mealService.dateSelected } }
      );
    } else if (
      errorType === OrderErrorType.StockInsufficientError ||
      errorType === OrderErrorType.StockOverflowError ||
      errorType === OrderErrorType.OverAmountLimitError ||
      errorType === OrderErrorType.OverCoreMenuLimitError
    ) {
      this.handleProductQuantityChange(
        productsFormattedFromError,
        productsFromCart
      );
    } else if (errorType === OrderErrorType.BasketValidationError) {
      this.removeProductsFromOrder(productsToRemove);
      this.router.navigate([`/meals/recap`], {
        queryParams: { date: this.mealService.dateSelected },
      });
    } else if (
      errorType === OrderErrorType.PickupPointInvalidError ||
      errorType === OrderErrorType.PickupTimeInvalidError ||
      errorType === OrderErrorType.PaymentMethodError ||
      errorType === OrderErrorType.WrongPaymentMethodError
    ) {
      // navigate to recap page
      this.router.navigate([`/meals/recap`], {
        queryParams: { date: this.mealService.dateSelected },
      });
    }
  }

  private removeProductsFromOrder(ids: string[]) {
    this.mealService.order.productsInOrder.forEach((product) => {
      if (ids.includes(product?.product?.id as string)) {
        this.mealService.order.removeProduct(product.product);
      }
    });
  }

  private handleProductQuantityChange(
    productsFormattedFromError: ProductsFormattedFromError[],
    productsFromCart: OrderProduct[]
  ) {
    productsFormattedFromError.forEach(({ productId, quantityLeft }) => {
      const products = productsFromCart.filter(
        (p) => p.product.id === productId
      );

      if (products.length > quantityLeft) {
        const excessQuantity = products.length - quantityLeft;

        for (let i = 0; i < excessQuantity; i++) {
          this.mealService.order.removeProduct(products[i].product);
        }
      }
    });
  }
}
